/**
 * Horizontal Bar Chart Component
 *
 * A reusable horizontal bar chart component that displays comparison data
 * between current and previous year with custom tooltips and percentage changes.
 * Uses Recharts with vertical layout for horizontal bars.
 */

// React and Recharts imports
import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';

// Styles and components
import styles from './HorizontalBarChart.module.scss';
import { CustomBarPath, CustomBarPathProps } from './CustomBarPath';
import { CustomLabel } from './CustomLabel';
import { CustomTooltip } from './CustomTooltip';

// Chart colors matching the design
const CHART_COLORS = {
    default: '#E8E8E8',
    highlighted: '#000000'
} as const;

// Data structure for horizontal bar chart
export interface HorizontalBarChartData {
    name: string;
    currentValue: number;
    previousValue: number;
    currentDate: string;
    previousDate: string;
    currentShare?: number;
    previousShare?: number;
}

interface HorizontalBarChartProps {
    data: HorizontalBarChartData[];
    title?: string;
    isCurrency?: boolean;
}

export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ data, title, isCurrency = false }) => {
    // State management - Track which bar is currently hovered
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    // Performance optimization - Calculate max value once and memoize
    const maxValue = useMemo(() => Math.max(...data.map(d => d.currentValue)), [data]);

    return (
        <div className={styles.chartContainer}>
            {/* Optional chart title */}
            {title && (
                <div className={styles.chartHeader}>
                    <h3 className={styles.chartTitle}>{title}</h3>
                </div>
            )}

            {/* Chart wrapper - Contains all chart rows */}
            <div className={styles.chartWrapper}>
                {data.map((item, index) => (
                    <div
                        key={index}
                        className={styles.chartRow}
                    >
                        {/* Row label - Category name displayed above the bar */}
                        <div className={styles.rowLabel}>{item.name}</div>

                        {/* Chart area - Handles hover events and displays the bar */}
                        <div
                            className={styles.rowChart}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        >
                            {/* Custom tooltip - Only shown when hovering over this bar */}
                            {hoveredIndex === index && (
                                <div className={styles.customTooltipWrapper}>
                                    <CustomTooltip
                                        active={true}
                                        payload={[{ payload: item }]}
                                        isCurrency={isCurrency}
                                    />
                                </div>
                            )}

                            {/* Recharts bar chart with vertical layout (horizontal bars) */}
                            <ResponsiveContainer
                                width="100%"
                                height={20}
                            >
                                <BarChart
                                    data={[item]}
                                    layout="vertical"
                                    margin={{ top: 10, right: 100, left: 0, bottom: 6 }}
                                >
                                    {/* X-axis configuration - Hidden, controls horizontal scale */}
                                    <XAxis
                                        type="number"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={false}
                                        domain={[0, maxValue]}
                                    />

                                    {/* Y-axis configuration - Hidden, used for vertical positioning */}
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={false}
                                        width={0}
                                    />

                                    {/* Bar configuration with custom shape and label */}
                                    <Bar
                                        dataKey="currentValue"
                                        radius={[0, 4, 4, 0]}
                                        barSize={20}
                                        label={props => (
                                            <CustomLabel
                                                {...props}
                                                data={[item]}
                                                isCurrency={isCurrency}
                                            />
                                        )}
                                        shape={(props: CustomBarPathProps) => (
                                            <CustomBarPath
                                                {...props}
                                                fill={
                                                    hoveredIndex === index
                                                        ? CHART_COLORS.highlighted
                                                        : CHART_COLORS.default
                                                }
                                            />
                                        )}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
